import { Buyer } from "../../../Models/Buyer";
import { BaseAPICallsHandler } from "../BaseAPICallsHandler";
import { NetworkUtils } from "../NetworkUtils";
import { ServerConfig } from "../ServerConfig";
import { SessionManager } from "../../SessionManager/SessionManager";
import { ErrorLogger } from "../../../utils/Logger";
import { HONEYPOT_FIELD_NAME } from "../../../utils/honeypot";
import NetworkErrorHandler from "../NetworkErrorHandler";

// Callers render the rejection value directly, so it must always be a string.
function toErrorMessage(error: any): string {
    if (typeof error === "string") return error;
    return error?.message || "Something went wrong. Please try again.";
}


export class AuthAPIHandler {
    
    public static shared = new AuthAPIHandler()
    private errorHandler = new NetworkErrorHandler()
    
    apiHandler = BaseAPICallsHandler.shared
    serverConfig = ServerConfig.shared
    networkUtils = NetworkUtils.shared
    sessionManager = SessionManager.shared

    // dependency injection here
    constructor();
    constructor(apiHandler: BaseAPICallsHandler, serverConfig: ServerConfig, networkUtils: NetworkUtils);
    public constructor(...params: any[]) {
        if(params.length >= 3) {
            this.apiHandler = params[0];
            this.serverConfig = params[1];
            this.networkUtils = params[2];
        }
    };

    public async register(name: string, email: string, password: string, confirmPassword: string, otp: string, honeypot: string = ""): Promise<Buyer> {
        let url = this.serverConfig.authAPIs.registerAPI;

        try {
            
            const buyerResponse = await this.apiHandler.makePostRequest({}, url, {name, email, password, confirmPassword, otp, [HONEYPOT_FIELD_NAME]: honeypot}); 
            const buyer = this.performPostLoginFlow(buyerResponse)
            return Promise.resolve(buyer);

        } catch (err) {
            return Promise.reject(toErrorMessage(err));
        }
    }

    public async login(email: string, password: string, honeypot: string = ""): Promise<Buyer> {

        let url = this.serverConfig.authAPIs.loginAPI;

        try {

            const buyerResponse = await this.apiHandler.makePostRequest({}, url, {email, password, [HONEYPOT_FIELD_NAME]: honeypot});
            const buyer = this.performPostLoginFlow(buyerResponse)
            return Promise.resolve(buyer);

        } catch (err) {
            ErrorLogger.log(err, 'AuthAPIHandler.signIn');
            return Promise.reject(toErrorMessage(err));
        }
    }

    public getGoogleAuthPageURL(): string {
        return this.serverConfig.authAPIs.googleAuthAPI
    }

    public googleAuthHandler(responseString: string): Promise<Buyer> {
        try {

            const buyerResponse = JSON.parse(responseString);
            const buyer = this.performPostLoginFlow(buyerResponse)
            return Promise.resolve(buyer);

        } catch (err) {
            const errorMessage = this.errorHandler.handleError(err);
            return Promise.reject(errorMessage);
        }
    }

    public async requestOtpForEmailVerification(email: string, name: string, honeypot: string = ""): Promise<boolean> {
        let url = this.serverConfig.authAPIs.requestOtpForEmailVerificationAPI;

        try {
            const response = await this.apiHandler.makePostRequest({}, url, {email, fullName: name, [HONEYPOT_FIELD_NAME]: honeypot});
            return Promise.resolve(response.data.success);
        } catch (err) {
            ErrorLogger.log(err, 'AuthAPIHandler.requestOtpForEmailVerification');
            return Promise.reject(toErrorMessage(err));
        }
    }

    public performPostLoginFlow(buyerResponse: any): Buyer {
        const buyerData = buyerResponse.data.buyer;
        const jwtToken = buyerResponse.data.jwtToken;
        // A missing token means this was not a real authentication (e.g. the
        // backend's bot decoy response); never persist it as a session.
        if (!buyerData || !jwtToken) throw new Error("Login failed. Please try again.");
        this.sessionManager.saveJWTToken(jwtToken);
        const buyer = new Buyer(buyerData);
        return buyer;
    }

}